<script>
// Components
import appConfig from "@/app.config.json";
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import UserProfile from "@/router/views/users/user-profile"

// Helpers
import UserUtils from '@/helpers/users';
import DateUtils from '@/helpers/date';
import Storage from '@/helpers/storage';
import {authComputed} from "@/state/helpers";

// Service
import ReportsDataService from "@/services/ReportsDataService";
import UsersDataService from "@/services/UsersDataService"

// External Components
import DatePicker  from "vue2-datepicker";
import 'vue2-datepicker/index.css';
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';
import CoolLightBox from 'vue-cool-lightbox'
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css'


export default {
  name: "reports",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Reports Manager",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    DatePicker,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },
  computed: {
    ...authComputed
  },
  methods: {
    ...UserUtils,
    ...DateUtils,

    handlePageChange(value) {
      this.page = value;
      this.loadReports();
    },

    handleDisplayChange() {
      this.loadReports();
    },

    handleDateChange() {
      let now = new this.$moment();
      switch(this.filters.dates.selected) {
        case "always":
          this.filters.fromDate = new this.$moment('2021-01-01').format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "today":
          this.filters.fromDate = now.format('YYYY-MM-DD');
          this.filters.toDate = now.format('YYYY-MM-DD');
          break;
        case "yesterday":
          this.filters.fromDate = now.subtract(1, 'day').format('YYYY-MM-DD');
          this.filters.toDate = this.filters.fromDate;
          break;
        case "week":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(1, 'week').startOf('day').format('YYYY-MM-DD');
          break;
        case "curMonth":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('month').format('YYYY-MM-DD');
          break;
        case "last30Day":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(30, 'day').startOf('day').format('YYYY-MM-DD');
          break;
        case "lastMonth":
          this.filters.fromDate = now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.filters.toDate = now.endOf('month').format('YYYY-MM-DD');
          break;
        case "last3Month":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.subtract(3, 'month').startOf('day').format('YYYY-MM-DD');
          break;
        case "curYear":
          this.filters.toDate = now.format('YYYY-MM-DD');
          this.filters.fromDate = now.startOf('year').format('YYYY-MM-DD');
          break;
        case "custom":
          this.filters.fromDate = null;
          this.filters.toDate = null;
          break;
      }
    },

    getRequestParams() {
      const params = {};

      params["page"] = this.page - 1;
      params["size"] = this.pageSizes.selected;

      if (this.filters.dates.selected === 'custom' && this.filters.dates.dateRange !== null) {
        this.filters.fromDate = this.$moment(this.filters.dates.dateRange[0]).format("YYYY-MM-DD 00:00:00");
        this.filters.toDate = this.$moment(this.filters.dates.dateRange[1]).format("YYYY-MM-DD 23:59:59");
      }
      else if (this.filters.fromDate === null)
        this.handleDateChange();

      if (this.filters.toDate !== null)
        params['toDate'] = this.filters.toDate;

      if (this.filters.fromDate !== null)
        params['fromDate'] = this.filters.fromDate;

      params['reportType'] = this.filters.reportType.selected;
      params['status'] = this.filters.status.selected;

      if (this.$store.state.layout.timezone === 'utc') {
        params['tz'] = this.$moment.tz.guess();
      }

      return params;
    },

    reviewReport(reportId) {
      this.$router.push({
        name: 'reportDetails',
        params: { reportId }
      });
    },

    closeReport(report) {
      const params = {
        adminId: this.getUser.uid
      };
      ReportsDataService.close(report.reportId, params)
        .then(() => {
          this.$toast.success(this.$t("reports.close_success"));

          this.stats.closed++;
          if (report.status === 'R') {
            this.stats.reviewed--;
          } else if (report.status === 'W') {
            this.stats.waiting--;
          } else if (report.status === 'A') {
            this.stats.answered--;
          }
          report.status = "C";
          report.closedAt = this.formatDateString(null, "DD MMM YYYY HH:mm");
        })
        .catch((error) => {
          window.console.log(error);
          this.$toast.error(error.toString());
        });
    },

    getMediaThumb() {
      if (this.reports.length > 0)
        this.reports.forEach(report => {
          if (report.userPhoto && report.userPhoto.length > 0)
            Storage.getUserMedia(report.userId, report.userPhoto, 'report-user-'+report.reportId);

          if (report.reporterPhoto && report.reporterPhoto.length > 0)
            Storage.getUserMedia(report.reporterId, report.reporterPhoto, 'report-reporter-'+report.reportId);

        });
    },

    loadStats() {
      const params = this.getRequestParams();

      ReportsDataService
          .getStats(params)
          .then(response => {
            const { stats, reportsTypes, evolGraph } = response.data;
            this.stats = stats;

            this.typesGraph.series = reportsTypes.series;
            this.typesGraph.chartOptions = {...this.typesGraph.chartOptions, ...{
                labels: reportsTypes.labels
              }};

            this.$refs.reportsEvolChart.updateSeries([{
              name: "Created",
              type: 'bar',
              data: evolGraph.created
            },{
              name: "Closed",
              type: 'bar',
              data: evolGraph.closed
            }, {
              name: "Running",
              type: 'bar',
              data: evolGraph.running
            }]);
          })
          .catch(error => {
            window.console.log(error);
          });
    },

    loadReports() {
      this.isLoading = true;
      const params = this.getRequestParams();

      ReportsDataService
          .getList(params)
          .then(response => {
            const { items, totalItems } = response.data;
            this.reports = items;
            this.count = totalItems;
            this.isLoading = false;
            this.getMediaThumb();
          })
          .catch(error => {
            window.console.log(error);
            this.$toast.error(this.$t("reports.errorNoResult"));
            this.isLoading = false;
          });
    },

    processFilters() {
      this.loadReports();
      this.loadStats();
    },

    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user '+userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        }
        catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = userId; }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    }
  },
  mounted() {
    if (this.getRole === 'operator') {
      this.filters.status.selected = '!C';
    }
    this.processFilters();
  },
  data() {
    return {
      isLoading: false,
      title: this.$t("menuitems.support.reports"),
      typesGraph: {
        series:[],
        chartOptions: {
          chart: {
            id: 'reports-type',
            type: 'donut',
          },
          dataLabels: {
            enabled: true
          },
          labels:[],
          noData: {
            text: 'Loading...'
          },
          legend: {
            position: 'bottom'
          },
        }
      },
      evolGraph: {
        series: [],
        chartOptions: {
          chart: {
            stacked: true,
            toolbar: {
              show: true,
            },
            zoom: {
              enabled: true,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              endingShape: "rounded",
            },
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            type: 'datetime',
            labels: {
              show: true,
              style: {
                fontSize: 11
              },
              hideOverlappingLabels: true
            }
          },
          yaxis: [{
            axisTicks: {
              show: true
            },
            title: {
              text: "Nb"
            }
          }],
          colors: ["#f46a6a", "#34c38f", "#f1b44c"],
          legend: {
            position: "bottom",
          },
          fill: {
            opacity: 1,
          },
        },
      },
      stats: {
        opened: '-',
        reviewed: '-',
        closed: '-',
        waiting: '-',
        answered: '-',
        avgClosedTime: '-',
      },
      reports: [],
      page: 1,
      count: 0,
      pageSizes: {
        options: ['20', '50', '100'],
        selected: '50'
      },
      filters: {
        fromDate: null,
        toDate: null,
        dates: {
          dateRange: null,
          selected: 'last30Day',
          options: [
            {value:'today', text:this.$t("dates.today")},
            {value:'yesterday', text:this.$t("dates.yesterday")},
            {value:'week', text:this.$t("dates.week")},
            {value:'curMonth', text:this.$t("dates.curMonth")},
            {value:'last30Day', text:this.$t("dates.last30Day")},
            {value:'lastMonth', text:this.$t("dates.lastMonth")},
            {value:'last3Month', text:this.$t("dates.last3Month")},
            {value:'curYear', text:this.$t("dates.curYear")},
            {value:'always', text:this.$t("dates.always")},
            {value:'custom', text:this.$t("dates.custom")}
          ]
        },
        reportType: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("reports.type_all")},
            {value: 'stolen', text: this.$t("reports.type_stolen")},
            {value: 'inappropriate_content', text: this.$t("reports.type_inappropriate_content")},
            {value: 'rude_abusive', text: this.$t("reports.type_rude_abusive")},
            {value: 'scam', text: this.$t("reports.type_scam")},
            {value: 'spam', text: this.$t("reports.type_spam")},
            {value: 'nudity', text: this.$t("reports.type_nudity")},
            {value: 'violence', text: this.$t("reports.type_violence")},
          ]
        },
        status: {
          selected: 'all',
          options: [
            {value: 'all', text: this.$t("reports.status_all")},
            {value: '!C', text: this.$t("reports.status_not_closed")},
            {value: 'O', text: this.$t("reports.status_O")},
            {value: 'R', text: this.$t("reports.status_R")},
            {value: 'C', text: this.$t("reports.status_C")},
            {value: 'W', text: this.$t("reports.status_W")},
            {value: 'A', text: this.$t("reports.status_A")},
          ]
        }
      },
      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Filters -->
      <div class="card">
        <div class="card-body row">
          <b-form inline class="row gy-2 gx-3 align-items-center" id="reportsFiltersForm" @submit.stop.prevent>

            <!-- dates -->
            <div class="col-sm-auto d-flex">
              <b-form-select
                  id="reportsDatesFilter"
                  @change="handleDateChange"
                  v-model="filters.dates.selected"
                  class="form-select me-lg-1"
                  :options="filters.dates.options">
              </b-form-select>
              <date-picker
                  v-show="filters.dates.selected === 'custom'"
                  :lang="page.localePicker"
                  :placeholder="this.$t('choose_date_interval')"
                  type="date"
                  value-type="format"
                  show-week-number
                  v-model="filters.dates.dateRange"
                  class="me-lg-2"
                  range append-to-body ></date-picker>

            </div>

            <!-- ReportType -->
            <div class="col-sm-auto">
              <b-form-select
                  id="reportsTypeFilter"
                  v-model="filters.reportType.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="ct in filters.reportType.options" :key="ct.value" :value="ct.value">{{ ct.text }}</option>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="col-sm-auto">
              <b-form-select
                  id="reportsStatusFilter"
                  v-model="filters.status.selected"
                  class="form-select me-lg-1"
              >
                <option v-for="st in filters.status.options" :key="st.value" :value="st.value">{{ st.text }}</option>
              </b-form-select>
            </div>

            <div class="col-sm-auto">
              <b-button variant="primary" class="w-md" @click="processFilters">{{ $t("find") }}</b-button>
            </div>
          </b-form>
        </div>
      </div>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <!-- Stats Plain -->
      <div class="row">
        <!-- Opened -->
        <div class="col-lg-2">
          <div class="card mini-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("reports.opened") }}</p>
                  <h5 class="mb-0">{{ stats.opened }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-danger rounded-circle white font-size-20"
                  >
                    <i class="bx bxs-file-plus"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Reviewed -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid ">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("reports.reviewed") }}</p>
                  <h5 class="mb-0">{{ stats.reviewed }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-warning text-white rounded-circle font-size-20"
                  >
                    <i class="bx bxs-file"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Waiting -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid ">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("reports.waiting") }}</p>
                  <h5 class="mb-0">{{ stats.waiting }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-info text-white rounded-circle font-size-20"
                  >
                    <i class="bx bx-mail-send"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Answered -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid ">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("reports.answered") }}</p>
                  <h5 class="mb-0">{{ stats.answered }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-pink text-white rounded-circle font-size-20"
                  >
                    <i class="bx bx-import"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Closed -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("reports.closed") }}</p>
                  <h5 class="mb-0">{{ stats.closed }}</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-success rounded-circle text-white font-size-20"
                  >
                    <i class="bx bxs-badge-check"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- AVG Time -->
        <div class="col-lg-2">
          <div class="card blog-stats-wid">
            <div class="card-body">
              <div class="d-flex flex-wrap">
                <div class="me-3">
                  <p class="text-muted mb-2">{{ $t("reports.avgClosedTime") }}</p>
                  <h5 class="mb-0">{{ stats.avgClosedTime }}h</h5>
                </div>

                <div class="avatar-sm ms-auto">
                  <div
                      class="avatar-title bg-light rounded-circle text-body font-size-20"
                  >
                    <i class="bx bxs-timer"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row">
        <!-- graph 1 -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body" id="cardReportsTypeChart">
              <h5 class="card-title">Types</h5>
              <apexchart
                  ref="ticketsTypesChart"
                  height="400"
                  type="donut"
                  dir="ltr"
                  :series="typesGraph.series"
                  :options="typesGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
        <!-- graph 1 -->
        <div class="col-12 col-lg-8">
          <div class="card">
            <div class="card-body" id="cardReportsEvolChart">
              <h5 class="card-title">Evolution</h5>
              <apexchart
                  ref="reportsEvolChart"
                  height="350"
                  type="bar"
                  dir="ltr"
                  :series="evolGraph.series"
                  :options="evolGraph.chartOptions"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Top list tools -->
      <div class="row mb-3">
        <!-- Display settings -->
        <div class="col-lg-auto col-12">
          <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
            <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
          </b-select>

          {{ count }} {{ $t('resultsCount') }}
        </div>
        <!-- Pagination -->
        <div class="col-lg-auto col-12">
          <div class="m-1 pagination-rounded">
            <b-pagination
                v-model="page"
                :total-rows="count"
                :per-page="pageSizes.selected"
                first-number
                last-number
                @change="handlePageChange"
                aria-controls="reports-list"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Result list -->
      <div class="row" v-if="!isLoading && reports.length > 0">
        <div class="table-responsive">
          <table
              class="table project-list-table table-nowrap table-centered table-borderless align-middle"
          >
            <thead>
            <tr>
              <th scope="col">{{ $t('reports.createdAt') }}</th>
              <th scope="col">{{ $t('reports.type') }}</th>
              <th scope="col">{{ $t('reports.user') }}</th>
              <th scope="col">{{ $t('reports.reporter') }}</th>
              <th scope="col">{{ $t('reports.message') }}</th>
              <th scope="col">{{ $t('reports.messages') }}</th>
              <th scope="col">{{ $t('reports.attachments') }}</th>
              <th scope="col">{{ $t('reports.lastActionAt') }}</th>
              <th scope="col">{{ $t('reports.status') }}</th>
              <th scope="col">{{ $t('action') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="report in reports" :key="report.reportId">
              <td>
                <p class="text-muted font-size-12">{{ formatDateString(report.createdAt, 'DD MMM YYYY HH:mm') }}</p>
              </td>
              <td>
                <p class="text-muted font-size-12 text-primary">{{ $t(`reports.type_${report.reportType}`) }}</p>
              </td>
              <td>
                <div class="media cursor-pointer" @click="showUser(report.userId)">
                  <div class="avatar-xs">
                    <img
                        :id="`report-user-${report.reportId}`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-12 mb-0">
                      <span
                          :class="{
                          'text-primary': report.userSex === 1,
                          'text-danger': report.userSex === 2,
                          'text-dark': report.userSex === 0,
                        }"
                      >
                        {{ report.userName || '-' }}
                      </span> {{ getAge(report.userBirth) }}</h5>
                    <small class="text-muted">#{{ report.userId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <div class="media cursor-pointer" @click="showUser(report.reporterId)">
                  <div class="avatar-xs">
                    <img
                        :id="`report-reporter-${report.reportId}`"
                        :src="require('@/assets/images/users/default-user.png')"
                        class="d-flex rounded-circle avatar-xs" />
                  </div>
                  <div class="media-body ms-1">
                    <h5 class="font-size-12 mb-0">
                      <span
                          :class="{
                          'text-primary': report.reporterSex === 1,
                          'text-danger': report.reporterSex === 2,
                          'text-dark': report.reporterSex === 0,
                        }"
                      >
                        {{ report.reporterName || '-' }}
                      </span> {{ getAge(report.reporterBirth) }}</h5>
                    <small class="text-muted">#{{ report.reporterId }}</small>
                  </div>
                </div>
              </td>
              <td>
                <p class="text-muted mb-0">{{ report.message | truncate(75) }}</p>
              </td>
              <td><i class="mdi mdi-message"></i> {{ report.messagesCount }}</td>
              <td><i class="mdi mdi-paperclip"></i> {{ report.attachmentsCount }}</td>
              <td>
                {{ report.lastActionAt == null || report.lastActionAt === '' ? '-' : formatDateString(report.lastActionAt, 'DD MMM YYYY HH:mm') }}
              </td>
              <td>
                    <span
                        class="badge"
                        :class="{
                          'bg-success': `${report.status}` === 'C',
                          'bg-warning': `${report.status}` === 'R',
                          'bg-danger': `${report.status}` === 'O',
                          'bg-info': `${report.status}` === 'W',
                          'bg-pink': `${report.status}` === 'A',
                        }"
                    >{{ $t(`reports.status_${report.status}`) }}</span
                    >
              </td>
              <td>
                <b-dropdown
                    class="card-drop"
                    variant="white"
                    menu-class="dropdown-menu-end"
                    right
                    toggle-class="p-0"
                >
                  <template v-slot:button-content>
                    <i class="mdi mdi-dots-horizontal font-size-18"></i>
                  </template>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="reviewReport(report.reportId)"
                  >{{ $t("reports.review") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(report.userId)"
                  >{{ $t("show_user") }}</b-dropdown-item>
                  <b-dropdown-item
                      href="javascript: void(0);"
                      @click="showUser(report.reportId)"
                  >{{ $t("show_reporter") }}</b-dropdown-item>
                  <b-dropdown-divider />
                  <b-dropdown-item v-show="report.status !== 'C' && report.status !== 'O'"
                                   href="javascript: void(0);"
                                   @click="closeReport(report)"
                  >{{ $t("close") }}</b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Result Empty -->
      <div class="row" v-if="!isLoading && reports.length === 0">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Empty Result</h4>
              <p>No reports were found for these filters. Try again.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- bottom list tools -->
      <div class="row mb-3">
      <!-- Display settings -->
      <div class="col-lg-auto col-12">
        <b-select class="form-select d-inline w-auto m-1" v-model="pageSizes.selected" @change="handleDisplayChange">
          <option v-for="size in pageSizes.options" :key="size" :value="size">{{ size }} {{ $t('pageSize') }}</option>
        </b-select>
      </div>
      <!-- Pagination -->
      <div class="col-lg-auto col-12">
        <div class="m-1 pagination-rounded">
          <b-pagination
              v-model="page"
              :total-rows="count"
              :per-page="pageSizes.selected"
              first-number
              last-number
              @change="handlePageChange"
              aria-controls="reports-list"
          ></b-pagination>
        </div>
      </div>
    </div>

    </div>

    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <CoolLightBox
        :items="userMedia.media"
        :index="userMedia.index"
        :effect="'fade'"
        @close="userMedia.index = null">
    </CoolLightBox>

  </Layout>
</template>

<style>
.cursor-pointer {
  cursor: pointer;
}

.b-sidebar-right {
  width:35%;
}

.b-sidebar-body {
  padding:16px;
}
</style>
